import { useMemo, useRef } from "react";
import { asNumber, formatValue } from "../../lib/kernelFormat.js";
import { boundedLimit, reconcileWaterfall } from "../../lib/decisionScales.js";
import { KernelEmpty, useElementSize } from "./shared.jsx";

export const kind = "waterfall";

function tone(delta, favorable) {
    if (!["up", "down"].includes(favorable) || delta === 0) return "var(--muted)";
    const good = favorable === "down" ? delta < 0 : delta > 0;
    return good ? "var(--green)" : "var(--red, #c0392b)";
}

export function Component({ spec, data, fillHeight = false, onFocus }) {
    const wrapperRef = useRef(null);
    const { width } = useElementSize(wrapperRef);
    const maxItems = boundedLimit(spec.maxItems, 10, 16);
    const height = boundedLimit(spec.height, 230, 420, 140);
    const layout = useMemo(() => {
        if (!data?.start || !data?.end) return null;
        const start = asNumber(data.start.value);
        const end = asNumber(data.end.value);
        if (start == null || end == null) return null;
        const originalSteps = (Array.isArray(data.steps) ? data.steps : [])
            .map((step) => ({ ...step, delta: asNumber(step?.delta) }))
            .filter((step) => step.delta != null);
        const shown = originalSteps.slice(0, maxItems);
        const tail = originalSteps.slice(maxItems);
        if (tail.length) {
            shown.push({ label: `Other (${tail.length})`, delta: tail.reduce((sum, step) => sum + step.delta, 0), folded: true });
        }
        let running = start;
        const columns = [{ type: "total", label: String(data.start.label || "Start"), base: 0, top: running, value: running }];
        for (const step of shown) {
            const delta = step.delta;
            const base = running;
            running += delta;
            columns.push({ type: "step", label: String(step?.label || "Unlabelled"), base, top: running, value: delta, folded: step.folded });
        }
        columns.push({ type: "total", label: String(data.end.label || "End"), base: 0, top: end, value: end });
        const extent = columns.reduce((acc, column) => ({
            min: Math.min(acc.min, column.base, column.top),
            max: Math.max(acc.max, column.base, column.top),
        }), { min: 0, max: 0 });
        return {
            columns,
            extent,
            reconciliation: reconcileWaterfall({ start: { value: start }, end: { value: end }, steps: originalSteps }),
        };
    }, [data, maxItems]);

    if (!layout || layout.columns.length <= 2) return <KernelEmpty message={spec.empty || "No changes to show"} />;
    const topPad = 20;
    const bottomPad = 28;
    const plotHeight = Math.max(20, height - topPad - bottomPad);
    const span = layout.extent.max - layout.extent.min || 1;
    const y = (value) => topPad + ((layout.extent.max - value) / span) * plotHeight;
    const gap = 7;
    const columnWidth = width > 0 ? Math.max(5, (width - gap * (layout.columns.length - 1)) / layout.columns.length) : 0;
    const format = spec.format || "number";
    const focused = spec.focusValue == null ? null : String(spec.focusValue);
    const interactive = Boolean(onFocus);
    const favorable = ["up", "down", "neutral"].includes(spec.favorable) ? spec.favorable : "neutral";

    return (
        <div className={fillHeight ? "flex h-full min-h-0 flex-col" : ""}>
            {spec.title ? <div className="mb-[4px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">{spec.title}</div> : null}
            <div ref={wrapperRef} className="w-full" style={{ height: `${height}px` }}>
                {width > 0 ? (
                    <svg width="100%" height={height} role={interactive ? "group" : "img"} aria-label={spec.title || "Waterfall chart"}>
                        <line x1="0" y1={y(0)} x2={width} y2={y(0)} stroke="var(--line)" strokeWidth="1" />
                        {layout.columns.map((column, index) => {
                            const x = index * (columnWidth + gap);
                            const barY = y(Math.max(column.base, column.top));
                            const barHeight = Math.max(1, Math.abs(y(column.base) - y(column.top)));
                            const total = column.type === "total";
                            const selected = focused != null && focused === column.label;
                            const dimmed = focused != null && !total && !selected;
                            const clickable = Boolean(onFocus && !total && !column.folded);
                            const activate = () => onFocus(spec.groupField || "label", selected ? null : column.label);
                            return (
                                <g
                                    key={`${column.label}-${index}`}
                                    role={clickable ? "button" : undefined}
                                    tabIndex={clickable ? 0 : undefined}
                                    aria-pressed={clickable ? selected : undefined}
                                    aria-label={clickable ? `${column.label}: ${formatValue(column.value, format)}` : undefined}
                                    opacity={dimmed ? 0.3 : 1}
                                    onClick={clickable ? activate : undefined}
                                    onKeyDown={clickable ? (event) => {
                                        if (event.key !== "Enter" && event.key !== " ") return;
                                        event.preventDefault();
                                        activate();
                                    } : undefined}
                                    style={clickable ? { cursor: "pointer" } : undefined}
                                >
                                    <rect x={x} y={barY} width={columnWidth} height={barHeight} fill={total ? "var(--ink)" : tone(column.value, favorable)} opacity={total ? 0.85 : 0.75} />
                                    {index < layout.columns.length - 1 ? <line x1={x + columnWidth} y1={y(column.top)} x2={x + columnWidth + gap} y2={y(column.top)} stroke="var(--line-strong)" strokeWidth="1" /> : null}
                                    <text x={x + columnWidth / 2} y={Math.max(9, barY - 4)} textAnchor="middle" className="fill-[color:var(--muted)]" style={{ fontSize: 9, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                                        {total ? formatValue(column.value, format) : `${column.value > 0 ? "+" : column.value < 0 ? "-" : ""}${formatValue(Math.abs(column.value), format)}`}
                                    </text>
                                    <text x={x + columnWidth / 2} y={height - 8} textAnchor="middle" className="fill-[color:var(--muted-soft)]" style={{ fontSize: 9 }}>
                                        {column.label.length > 12 ? `${column.label.slice(0, 11)}...` : column.label}
                                    </text>
                                </g>
                            );
                        })}
                    </svg>
                ) : null}
            </div>
            <div className="mt-[3px] text-[9.5px] text-[color:var(--muted-soft)]">
                {layout.reconciliation.reconciles
                    ? "The changes reconcile to the stated end value."
                    : `Changes do not reconcile; drift is ${formatValue(layout.reconciliation.drift, format)}.`}
            </div>
        </div>
    );
}
